import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'

import { useSession } from '../../../../../contexts/SessionContext'
import { useDirectDebitStatus } from '../../../../../contexts/session/DirectDebitContext'
import { completeSessionOtpVerification } from '../../../../../services/SessionService'
import { OTP_LENGTH, OTP_RESEND_COOLDOWN_SECS, OTP_MAX_ATTEMPTS } from '../../../../../constants/DirectDebitConstants'
import { maskEmail, maskPhone } from '../../../../../utils/directDebitFormatting'
import useSubmitMandate from '../useSubmitMandate'

import BottomSheet from '../../../../elements/direct-debit/BottomSheet'
import OTPInput from '../../../../elements/direct-debit/OTPInput'
import Chip from '../../../../elements/direct-debit/Chip'
import DDIcon from '../../../../icons/DDIcon'
import Toast from '../../../../elements/direct-debit/Toast'
import styles from './EnterCodeSheet.module.css'

const VERIFIED_HOLD_MS = 1150

/**
 * Spec §6 screens 03/03b/03c/03d - new-user code-entry/checking/verified/wrong/resend sheet.
 * (The spec also reuses this sheet for the returning-user flow's R2 - not built, see
 * DirectDebitContext.js's note.)
 */
const EnterCodeSheet = ({ isOpen, onClose }) => {
  const { startOtpVerification } = useSession()
  const {
    userEmail,
    userPhone,
    otpChannel,
    setOtpChannel,
    otpRequestedAt,
    setOtpRequestedAt,
    setOtpSessionId,
    sessionReference,
    sessionKey,
    otpAttemptsLeft,
    setOtpAttemptsLeft,
  } = useDirectDebitStatus()
  const submitMandate = useSubmitMandate()

  const [otp, setOtp] = useState('')
  const [status, setStatus] = useState('default')
  const [secondsLeft, setSecondsLeft] = useState(OTP_RESEND_COOLDOWN_SECS)
  const [toast, setToast] = useState({ show: false, message: '' })

  const isPhoneChannel = otpChannel === 'sms'
  // userPhone is the bare national-subscriber digits (no "0"/"+234" - see PhoneInput).
  // Country isn't tracked past YourDetailsScreen, but NG (+234) is the only validated
  // country today, so both forms are reconstructed with that assumption here too.
  const phoneForBackend = `+234${userPhone}`
  const maskedPhone = maskPhone(`0${userPhone}`)

  useEffect(() => {
    if (!isOpen) {
      setOtp('')
      setStatus('default')
    }
  }, [isOpen])

  useEffect(() => {
    if (!otpRequestedAt || !isOpen) return undefined
    const tick = () => {
      const elapsed = Math.floor((Date.now() - new Date(otpRequestedAt).getTime()) / 1000)
      setSecondsLeft(Math.max(0, OTP_RESEND_COOLDOWN_SECS - elapsed))
    }
    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [otpRequestedAt, isOpen])

  const destination = isPhoneChannel ? maskedPhone : maskEmail(userEmail)

  const handleOtpChange = (value) => {
    setOtp(value)
    if (status === 'wrong') setStatus('default')
  }

  const handleComplete = async (code) => {
    setStatus('checking')
    try {
      const response = await completeSessionOtpVerification({ sessionKey, otpCode: code })
      if (response.data?.status === 'VERIFIED') {
        setStatus('verified')
        // Hold on the verified state briefly, then submit the mandate WHILE the sheet is
        // still open (and closeBlocked, so Esc/backdrop-click can't dismiss it either) -
        // closing first and submitting after left "Confirm it's you" visible, with its
        // Proceed button still clickable, for the whole mandate round-trip. A second click
        // during that window re-ran updateSessionData/startOtpVerification and looped.
        // By the time we close here, a successful submit has already moved
        // directDebitStatus past YOUR_DETAILS, so the form is never shown again; a failed
        // submit (handled inside useSubmitMandate) surfaces its error there instead, which
        // is the one case we do want the user back on the form.
        await new Promise((resolve) => setTimeout(resolve, VERIFIED_HOLD_MS))
        setStatus('submitting')
        await submitMandate()
        onClose()
      } else {
        failAttempt()
      }
    } catch {
      failAttempt()
    }
  }

  const failAttempt = () => {
    setOtpAttemptsLeft((n) => Math.max(0, n - 1))
    setOtp('')
    setStatus('wrong')
  }

  const sendOtp = async (channel) => {
    const response = await startOtpVerification(sessionKey, {
      verificationType: channel.toUpperCase(),
      email: userEmail,
      phoneNumber: phoneForBackend,
      channel,
      reference: sessionReference,
    })
    if (response.statusCode === '0') {
      const otpSession = response.data.otpSessions?.[0]
      if (otpSession) setOtpSessionId(otpSession.otpSessionId)
      setOtpRequestedAt(new Date())
    }
    return response
  }

  const handleResend = async () => {
    if (secondsLeft > 0) return
    setOtp('')
    setStatus('default')
    setOtpAttemptsLeft(OTP_MAX_ATTEMPTS)
    await sendOtp(otpChannel)
    setToast({ show: true, message: 'New code sent' })
  }

  const handleSwitchChannel = async () => {
    const nextChannel = isPhoneChannel ? 'email' : 'sms'
    setOtpChannel(nextChannel)
    setOtp('')
    setStatus('default')
    setOtpAttemptsLeft(OTP_MAX_ATTEMPTS)
    await sendOtp(nextChannel)
    setToast({ show: true, message: nextChannel === 'sms' ? 'Code sent by SMS' : 'Code sent to your email' })
  }

  const locked = status === 'wrong' && otpAttemptsLeft === 0

  const chip = () => {
    if (status === 'checking') return <Chip icon={<DDIcon name='spinner' />}>Checking</Chip>
    if (status === 'verified') return <Chip variant='success' icon={<DDIcon name='verified-fill' />}>Verified</Chip>
    if (status === 'submitting') return <Chip variant='success' icon={<DDIcon name='spinner' />}>Setting up your mandate…</Chip>
    if (status === 'wrong') {
      return (
        <Chip variant='error' icon={<DDIcon name='helper-error' />}>
          {locked ? 'Too many wrong tries' : `Wrong code · ${otpAttemptsLeft} ${otpAttemptsLeft === 1 ? 'try' : 'tries'} left`}
        </Chip>
      )
    }
    return null
  }

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      icon='sheet-scan'
      title='Enter your code'
      description={
        <>
          We sent a {OTP_LENGTH}-digit code by {isPhoneChannel ? 'SMS to' : 'email to'} {destination}
        </>
      }
      closeBlocked={status === 'checking' || status === 'submitting'}
    >
      <OTPInput
        value={otp}
        onChange={handleOtpChange}
        length={OTP_LENGTH}
        status={
          status === 'checking' ? 'checking' : status === 'verified' || status === 'submitting' ? 'verified' : 'default'
        }
        onComplete={handleComplete}
        autoFocus
      />
      {status !== 'default' && (
        <div className={styles.row} role='status' aria-live='polite'>
          {chip()}
        </div>
      )}

      <div className={styles.resendRow}>
        {locked ? (
          <button type='button' className={styles.switchLink} onClick={handleResend}>
            Send me a new code
          </button>
        ) : secondsLeft > 0 ? (
          <span className={styles.resendText}>
            Didn't get it? <span className={styles.resendTimer}>Resend code in {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}s</span>
          </span>
        ) : (
          <button type='button' className={styles.switchLink} onClick={handleResend}>
            Resend code
          </button>
        )}
      </div>

      {!isPhoneChannel && (
        <div className={styles.emailNotice}>
          <DDIcon name='helper-warning' />
          <span>Email may take 1–2 minutes. Check spam too.</span>
        </div>
      )}

      <button type='button' className={styles.switchLink} onClick={handleSwitchChannel}>
        <DDIcon name={isPhoneChannel ? 'switch-channel-email' : 'switch-channel-sms'} />
        {isPhoneChannel ? 'Email the code instead' : `Text the code to ${maskedPhone} instead`}
      </button>

      <Toast show={toast.show} message={toast.message} onDismiss={() => setToast({ show: false, message: '' })} />
    </BottomSheet>
  )
}

EnterCodeSheet.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default EnterCodeSheet
